/** Create/edit form for a Package (unified store). Validated against `createPackageSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  PACKAGE_TYPES,
  TARGET_LEVELS,
  PACKAGE_MATURITY_STAGES,
  EXECUTOR_TYPES,
  ARTIFACT_TYPES,
  UNKNOWN_TRANSITION_MODES,
  createPackageSchema,
  type CreatePackageInput,
  type PackageType,
} from "@aos/shared";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import { Label } from "@/components/ui/label";

import { FormShell } from "../skills/_components/FormShell";
import {
  IntCheckboxField,
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface PackageFormProps {
  action: "create" | "edit";
}

export function PackageForm({ action }: PackageFormProps) {
  const form = useForm<CreatePackageInput>({
    resolver: zodResolver(createPackageSchema),
    refineCoreProps: { resource: "packages", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
    watch,
    setValue,
  } = form;

  const packageType = watch("package_type") as PackageType | undefined;
  const body = watch("body_markdown") ?? "";

  const isSkill = packageType === "skill";
  const isContour = packageType === "contour";

  return (
    <FormShell
      title={action === "create" ? "New Package" : "Edit Package"}
      resource="packages"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="name"
        label="Name"
        placeholder="Package name"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="package_type"
          label="Package Type"
          options={optionsFromEnum(PACKAGE_TYPES)}
        />
        <SelectField
          form={form}
          name="target_level"
          label="Target Level"
          options={optionsFromEnum(TARGET_LEVELS)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="maturity_stage"
          label="Maturity Stage"
          options={optionsFromEnum(PACKAGE_MATURITY_STAGES)}
          allowEmpty
        />
        <TextField
          form={form}
          name="status"
          label="Status"
          placeholder="draft"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="domain"
          label="Domain"
          placeholder="e.g. operations"
        />
        <TextField
          form={form}
          name="department"
          label="Department"
          placeholder="e.g. estimating"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="owner_user_id"
          label="Owner User ID"
          placeholder="user_…"
        />
        <NumberField form={form} name="version" label="Version" placeholder="1" />
      </div>
      <TextAreaField
        form={form}
        name="description"
        label="Description"
        placeholder="What this package represents"
      />
      <div className="space-y-1.5">
        <Label htmlFor="body_markdown">Body (Markdown)</Label>
        <MarkdownEditor
          value={body}
          onChange={(next) =>
            setValue("body_markdown", next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </div>
      <TextField
        form={form}
        name="source_path"
        label="Source Path"
        placeholder="rulebase/packages/…"
      />

      {/* Skill-typed columns — relevant when package_type=skill. */}
      {isSkill ? (
        <div className="space-y-4 rounded-lg border bg-muted/40 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Skill contract
          </p>
          <TextAreaField
            form={form}
            name="short_summary"
            label="Short Summary"
            placeholder="One-line summary of what this skill does"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextAreaField
              form={form}
              name="input_contract"
              label="Input Contract"
              placeholder="JSON / description of expected inputs"
              rows={3}
            />
            <TextAreaField
              form={form}
              name="output_contract"
              label="Output Contract"
              placeholder="JSON / description of produced outputs"
              rows={3}
            />
          </div>
          <SelectField
            form={form}
            name="allowed_executor_type"
            label="Allowed Executor"
            options={optionsFromEnum(EXECUTOR_TYPES)}
            allowEmpty
          />
        </div>
      ) : null}

      {/* Contour-typed columns — relevant when package_type=contour. */}
      {isContour ? (
        <div className="space-y-4 rounded-lg border bg-muted/40 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Contour behavior
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              form={form}
              name="object_type"
              label="Object Type"
              options={optionsFromEnum(ARTIFACT_TYPES)}
              allowEmpty
            />
            <NumberField
              form={form}
              name="rigidity_level"
              label="Rigidity Level"
              placeholder="3"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <IntCheckboxField
              form={form}
              name="auto_transition"
              label="Auto-transition (strong contour)"
              description="On = strong (auto phase moves); off = weak (mode scoring)."
            />
            <SelectField
              form={form}
              name="unknown_transition"
              label="Unknown Transition"
              options={optionsFromEnum(UNKNOWN_TRANSITION_MODES)}
              allowEmpty
            />
          </div>
          <TextAreaField
            form={form}
            name="modes"
            label="Modes (JSON)"
            placeholder='["exploring","drafting"]'
            rows={2}
            description="JSON array of weak-contour mode names."
          />
        </div>
      ) : null}
    </FormShell>
  );
}

export default PackageForm;
